/**
 * SkillFilters
 * Sidebar component for filtering and browsing skills
 */

import React from 'react';
import { Briefcase, ShoppingBag, LayoutGrid, ChevronDown } from 'lucide-react';
import { Input, Checkbox } from '../../ui';
import styles from './SkillFilters.module.css';

export type SkillBrowseCategory = 'all' | 'project' | 'marketplace';

export interface SkillFiltersProps {
  selectedBrowseCategory: SkillBrowseCategory;
  onBrowseCategoryChange: (category: SkillBrowseCategory) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedProject?: string;
  onProjectChange?: (project: string) => void;
  availableCategories: string[];
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  createdByMe: boolean;
  onCreatedByMeChange: (value: boolean) => void;
  selectedCreator?: string;
  onCreatorChange?: (creator: string) => void;
  visibilityFilter: 'all' | 'private' | 'project';
  onVisibilityFilterChange: (filter: 'all' | 'private' | 'project') => void;
}

export const SkillFilters: React.FC<SkillFiltersProps> = ({
  selectedBrowseCategory,
  onBrowseCategoryChange,
  searchQuery,
  onSearchChange,
  selectedProject,
  onProjectChange,
  availableCategories,
  selectedCategories,
  onCategoriesChange,
  createdByMe,
  onCreatedByMeChange,
  selectedCreator,
  onCreatorChange,
  visibilityFilter,
  onVisibilityFilterChange,
}) => {
  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <h1 className={styles.title}>Skills</h1>
        <p className={styles.subtitle}>
          Create and manage reusable knowledge for your assistants
        </p>
      </div>

      {/* BROWSE Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>BROWSE</h2>
        <nav className={styles.browseNav}>
          <button
            className={`${styles.browseItem} ${
              selectedBrowseCategory === 'all' ? styles.active : ''
            }`}
            onClick={() => onBrowseCategoryChange('all')}
          >
            <LayoutGrid size={18} />
            <span>All Skills</span>
          </button>
          <button
            className={`${styles.browseItem} ${
              selectedBrowseCategory === 'project' ? styles.active : ''
            }`}
            onClick={() => onBrowseCategoryChange('project')}
          >
            <Briefcase size={18} />
            <span>Project Skills</span>
          </button>
          <button
            className={`${styles.browseItem} ${
              selectedBrowseCategory === 'marketplace' ? styles.active : ''
            }`}
            onClick={() => onBrowseCategoryChange('marketplace')}
          >
            <ShoppingBag size={18} />
            <span>Marketplace</span>
          </button>
        </nav>
      </div>

      {/* FILTERS Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>FILTERS</h2>

        {/* Search */}
        <div className={styles.filterGroup}>
          <Input
            isSearch
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            fullWidth
          />
        </div>

        {/* Project Filter */}
        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>PROJECT</span>
          </button>
          <div className={styles.filterContent}>
            <select
              className={styles.select}
              value={selectedProject || ''}
              onChange={(e) => onProjectChange?.(e.target.value)}
            >
              <option value="">All Projects</option>
              <option value="current">Current Project</option>
            </select>
          </div>
        </div>

        {/* Categories Filter */}
        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>CATEGORIES</span>
          </button>
          <div className={styles.filterContent}>
            <div className={styles.categoriesList}>
              {availableCategories.map((cat) => (
                <div key={cat} className={styles.categoryItem}>
                  <Checkbox
                    checked={selectedCategories.includes(cat)}
                    onChange={(checked) => {
                      if (checked) {
                        if (selectedCategories.length < 3) {
                          onCategoriesChange([...selectedCategories, cat]);
                        }
                      } else {
                        onCategoriesChange(selectedCategories.filter((c) => c !== cat));
                      }
                    }}
                    label={cat}
                    disabled={!selectedCategories.includes(cat) && selectedCategories.length >= 3}
                  />
                </div>
              ))}
            </div>
            <p className={styles.filterHint}>Select up to 3 categories</p>
          </div>
        </div>

        {/* Created By Filter */}
        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>CREATED BY</span>
          </button>
          <div className={styles.filterContent}>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                checked={createdByMe}
                onChange={onCreatedByMeChange}
                label="Created by Me"
              />
            </div>
            <select
              className={styles.select}
              value={selectedCreator || ''}
              onChange={(e) => onCreatorChange?.(e.target.value)}
            >
              <option value="">Created By</option>
              <option value="user1">User 1</option>
              <option value="user2">User 2</option>
            </select>
          </div>
        </div>

        {/* Visibility Filter */}
        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>VISIBILITY</span>
          </button>
          <div className={styles.filterContent}>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="visibility"
                  value="all"
                  checked={visibilityFilter === 'all'}
                  onChange={(e) => onVisibilityFilterChange(e.target.value as any)}
                />
                <span>All</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibilityFilter === 'private'}
                  onChange={(e) => onVisibilityFilterChange(e.target.value as any)}
                />
                <span>Private</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="visibility"
                  value="project"
                  checked={visibilityFilter === 'project'}
                  onChange={(e) => onVisibilityFilterChange(e.target.value as any)}
                />
                <span>Project</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillFilters;
